import { useCallback, useRef, useEffect } from 'react';
import LogicFlow from '@logicflow/core';
import { DataSourceTableType } from '@/types/dataMap';
import { DataMapApi } from '@/api/dataMapApi';
import { message } from 'antd';
import { data } from 'vis-network';

interface UseDragAndDropProps {
  canvasRef: React.RefObject<HTMLDivElement>;
  lfRef: React.RefObject<LogicFlow | null>;
  mapViewsId: string | null;
}

const useDragAndDrop = ({
  canvasRef,
  lfRef,
  mapViewsId,
}: UseDragAndDropProps) => {
  // 使用useRef缓存mapViewsId，避免当mapViewsId变化时重新创建函数
  const mapViewsIdRef = useRef<string | null>(mapViewsId);

  // 当mapViewsId变化时，更新ref
  useEffect(() => {
    mapViewsIdRef.current = mapViewsId;
  }, [mapViewsId]);

  // 处理表拖拽开始
  const handleTableDragStart = useCallback(
    async (datasourceId: string, tableId: string, e: React.DragEvent) => {
      console.log('处理表拖拽开始', datasourceId, tableId, e);
      
      e.dataTransfer.setData(
        'text/plain',
        JSON.stringify({ datasourceId, tableId })
      );
      // 设置拖拽效果
      e.dataTransfer.effectAllowed = 'copy';
    },
    []
  );

  // 处理画布拖拽释放
  const handleCanvasDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();

      // 如果mapViewsId为空，不执行后续操作
      const currentMapViewsId = mapViewsIdRef.current;
      if (!currentMapViewsId) {
        console.log('mapViewsId为空，无法添加表');
        return;
      }

      console.log('handleCanvasDrop', e.dataTransfer.getData('text/plain'));

      // 获取拖拽的表名
      const paramsData = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (!paramsData) return;

      try {
        const res = await DataMapApi.getTableDetail(
          paramsData.datasourceId,
          paramsData.tableId
        );
        if (res.code === 200 && !!Object.keys(res?.data)?.length) {
          // 计算拖拽到画布的位置
          if (!canvasRef.current || !lfRef.current) return;

          // 获取画布元素的位置
          const rect = canvasRef.current.getBoundingClientRect();

          // 获取当前变换信息
          const transform = lfRef.current.getTransform();

          // 计算鼠标在浏览器窗口中的位置
          const clientX = e.clientX;
          const clientY = e.clientY;

          // 计算鼠标在画布容器内的相对位置（考虑固定偏移）
          const containerX = clientX - rect.left;
          const containerY = clientY - rect.top;

          // 处理画布的缩放和位移
          const modelX = containerX - transform.TRANSLATE_X;
          const modelY = containerY - transform.TRANSLATE_Y;

          // 创建节点数据
          const nodeData = {
            id: res?.data.id,
            type: 'sql-node',
            x: modelX,
            y: modelY,
            properties: {
              tableName: res?.data.tableName,
              tableId: res?.data.id,
              fields: res?.data.columns?.length
                ? res?.data.columns.map(col => ({
                    key: col.columnName,
                    type: col.dataType,
                    id: col.id,
                  }))
                : [],
            },
          };
          console.log('处理画布拖拽释放--nodeData', nodeData);

          // 调用API添加表映射，确保mapViewsId有值
          if (paramsData.tableId) {
            const addRes = await DataMapApi.mappingsAddTable(
              paramsData.tableId,
              String(currentMapViewsId)
            );
            if (addRes?.code === 200) {
              // 添加节点到画布
              nodeData['id'] = String(addRes.data || '');
              nodeData['properties']['tableId'] = String(addRes.data || '');
              lfRef.current.addNode(nodeData);
              message.success('映射试图添加表数据成功');
            }
          }
        }
      } catch (error) {
        console.error('获取表信息失败', error);
      }
    },
    [canvasRef, lfRef]
  );

  // 处理画布拖拽悬停
  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  return {
    handleTableDragStart,
    handleCanvasDrop,
    handleCanvasDragOver,
  };
};

export default useDragAndDrop;
